import type { ToastServiceMethods } from 'primevue';
import type { ToastMessageOptions } from 'primevue/toast';

let _toastInstance: ToastServiceMethods | null = null;

export function setToastInstance(toast: ToastServiceMethods) {
  _toastInstance = toast;
}

export function getToastInstance(): ToastServiceMethods {
  if (!_toastInstance) {
    throw new Error('Toast not initialized. Call setToastInstance() from App.vue setup.');
  }
  return _toastInstance;
}

export enum ToastSeverity {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warn',
  ERROR = 'error',
}

export enum ToastLifetime {
  SHORT = 3000,
  MEDIUM = 5000,
  LONG = 10000,
}

export interface ToastLabels {
  /**
   * Opis radnje koja je pala, npr. "Greška pri preuzimanju dokumenta.". Ide u summary i time
   * čuva informaciju koju je poziv prije prikazivao sam; serverova poruka pada u detail.
   * Razlikuje se od `defaultError`, koji je samo fallback kad poruke nema.
   */
  context?: string;
  defaultError?: string;
  tracingIdPrefix?: string;
  copyErrorId?: string;
  errorIdCopied?: string;
  errorIdCopyFailed?: string;
}

// `context` je namjerno izvan defaulta - opisuje konkretnu radnju pa ga zna samo pozivatelj.
const DEFAULT_LABELS: Required<Omit<ToastLabels, 'context'>> = {
  defaultError: 'Došlo je do nepoznate greške',
  tracingIdPrefix: 'ID greške',
  copyErrorId: 'Kopiraj ID greške',
  errorIdCopied: 'ID greške je kopiran.',
  errorIdCopyFailed: 'Kopiranje nije uspjelo. Označite i kopirajte ID ručno.',
};

/**
 * Podaci o ID-u greške koje ErrorToastMessage prikazuje uz gumb za kopiranje.
 *
 * Prevedene labele putuju kroz `data` jer se prijevod razrješava u handleError-u
 * (gdje je i18n kontekst dostupan), a shared komponenta ne poznaje lokalizaciju aplikacije.
 */
export interface ErrorToastData {
  tracingId: string;
  tracingIdPrefix: string;
  copyLabel: string;
  copiedMessage: string;
  copyFailedMessage: string;
}

export type ErrorToastMessageOptions = ToastMessageOptions & {
  data?: ErrorToastData;
};

const ERROR_ID_HEADER = 'x-error-id';

/**
 * Backend vraća ID greške i u X-Error-Id headeru jer tijelo odgovora nije uvijek čitljivo -
 * kod preuzimanja datoteke axios čita odgovor kao Blob, pa `data.tracingId` ne postoji.
 * Header je tada jedini način da korisnik dobije ID koji može poslati supportu.
 */
function readTracingIdHeader(error: any): string {
  const headers = error.response?.headers;
  if (!headers) return '';

  const value =
    typeof headers.get === 'function' ? headers.get(ERROR_ID_HEADER) : headers[ERROR_ID_HEADER];

  return typeof value === 'string' ? value : '';
}

const DEDUPE_WINDOW_MS = 4000;
const _recentErrors = new Map<string, number>();

export function handleError(
  error: any,
  toast: ToastServiceMethods,
  labels?: ToastLabels,
) {
  const resolved = { ...DEFAULT_LABELS, ...labels };

  const statusCode = error.response?.status;
  const serverMessage = error.response?.data?.error;
  const detailsText = error.response?.data?.details?.join(' ') ?? '';
  const tracingId = error.response?.data?.tracingId ?? readTracingIdHeader(error);

  // `context` opisuje radnju koja je pala ("Greška pri preuzimanju dokumenta.") i uvijek ide u
  // summary, jer je to jedino što korisniku kaže ŠTO je pokušao. Serverova poruka je često
  // generična ("Došlo je do neočekivane greške.") pa se spušta u detail - ne gubi se, ali ne
  // preuzima naslov. Bez `context` ponašanje je nepromijenjeno: serverova poruka je summary.
  const summary = labels?.context ?? serverMessage ?? resolved.defaultError;
  const detail = [labels?.context ? serverMessage : null, detailsText]
    .filter(Boolean)
    .join(' ');

  const isServerError = statusCode >= 500 && statusCode < 600;
  // Zahtjev bez odgovora (prekid mreže, timeout, otkazan poziv) nije korisnička greška koju bi
  // korisnik mogao ispraviti, pa ostaje "error" kao i prije - samo bez sticky, jer nema ID za
  // kopiranje. Da ovo ide u "warn", pozivi koji su prije sami dizali error toast postali bi žuti.
  const hasNoResponse = statusCode === undefined;
  const severity =
    isServerError || hasNoResponse ? ToastSeverity.ERROR : ToastSeverity.WARNING;

  // Ne gomilaj iste greške (npr. više paralelnih poziva koji svi padnu).
  // U ključ ulazi i URL zahtjeva: paralelni pozivi istog endpointa se dalje spajaju u
  // jedan toast, ali dvije različite greške koje dijele generičku poruku
  // ("Došlo je do neočekivane greške.") ostaju odvojene - inače bi tracingId druge
  // greške nestao prije nego ga korisnik može poslati supportu.
  const now = Date.now();
  const dedupeKey = `${severity}:${summary}:${error.config?.url ?? ''}`;
  const last = _recentErrors.get(dedupeKey);
  if (last && now - last < DEDUPE_WINDOW_MS) {
    return;
  }
  _recentErrors.set(dedupeKey, now);

  // tracingId se namjerno NE ulijeva u detail - ide u `data` da ga ErrorToastMessage
  // može prikazati kao zaseban, kopirljiv element.
  const errorData: ErrorToastData | undefined = tracingId
    ? {
        tracingId,
        tracingIdPrefix: resolved.tracingIdPrefix,
        copyLabel: resolved.copyErrorId,
        copiedMessage: resolved.errorIdCopied,
        copyFailedMessage: resolved.errorIdCopyFailed,
      }
    : undefined;

  const toastMessage: ErrorToastMessageOptions = {
    severity,
    summary,
    detail,
    data: errorData,
    ...(isServerError
      ? { sticky: true, closable: true }
      : { life: ToastLifetime.MEDIUM }),
  };

  toast.add(toastMessage);
}

export function successToast(message: string, toast: ToastServiceMethods) {
  toast.add({
    severity: ToastSeverity.SUCCESS,
    summary: message,
    life: ToastLifetime.SHORT,
  });
}

// Same shape/severity/lifetime handleError() already uses for a non-server (4xx) error, for callers
// that want to show one specific, already-known message instead of parsing it out of a raw error
// (e.g. a business-rule conflict the caller wants worded on its own terms).
export function warningToast(message: string, toast: ToastServiceMethods) {
  toast.add({
    severity: ToastSeverity.WARNING,
    summary: message,
    life: ToastLifetime.MEDIUM,
  });
}
